import React from 'react';
import { EmptyStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { REFLECTOR_DOWN_TITLE } from '../../../helpers/bgpValidation';
import { ClusterMemberCpr } from '../helpers/bgpVisibilityApi';
import { RouteRow } from '../utils/routeModel';
import { BgpRouteTable } from './BgpRouteTable';

interface BgpTableViewProps {
  /** Rows after the active filters — what actually renders when there is something to show. */
  filteredRows: RouteRow[];
  /** Rows before the active filters — distinguishes "nothing returned" from "nothing matched". */
  allRows: RouteRow[];
  direction: 'INBOUND' | 'OUTBOUND';
  loading: boolean;
  isCluster: boolean;
  /** Cluster members withheld from the rows above (CTX-125); empty in reflector mode. */
  downMembers: ClusterMemberCpr[];
  /** True when the entity is a cluster and NO member is up — the terminal down state. */
  allMembersDown: boolean;
  /** Renders a member as "Primary CTXR-5cb248" for the notice / down copy. */
  formatMemberLabel: (member: ClusterMemberCpr) => string;
}

const CenteredState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex h-full items-center justify-center">
    <EmptyStateView title={title} description={description} />
  </div>
);

/**
 * Names the cluster members whose routes were withheld, so a table showing only the surviving
 * member's prefixes is never mistaken for the cluster's full picture (CTX-125).
 */
const DownMemberNotice: React.FC<{ labels: string[] }> = ({ labels }) => (
  <div className="mb-2 shrink-0 rounded-md border border-semantic-border-primary bg-semantic-bg-secondary px-3 py-2 text-xs text-semantic-text-secondary">
    {labels.join(', ')}
    {labels.length > 1 ? ' are down — their routes are not shown' : ' is down — its routes are not shown'}
  </div>
);

/**
 * Table mode for BGP Visibility: the route table plus its four terminal states (loading, every
 * cluster member down, nothing returned, nothing matching the filters), resolved as flat early
 * returns rather than a nested ternary chain in the page.
 *
 * Row filtering is the caller's job — this component renders exactly what it is handed. Down
 * members are already excluded upstream (`allMemberRows`), so their prefixes, metrics and filter
 * options never reach here; `downMembers` is passed only to explain the omission to the operator.
 */
export const BgpTableView: React.FC<BgpTableViewProps> = ({
  filteredRows,
  allRows,
  direction,
  loading,
  isCluster,
  downMembers,
  allMembersDown,
  formatMemberLabel
}) => {
  const downLabels = downMembers.map(formatMemberLabel);

  const body = (): React.ReactNode => {
    if (loading) {
      return <BgpRouteTable rows={[]} direction={direction} showReflectorColumn={isCluster} loading />;
    }

    if (allMembersDown) {
      return (
        <CenteredState
          title={REFLECTOR_DOWN_TITLE}
          description={`Every member of this cluster is down (${downLabels.join(', ')}) — no route data could be verified.`}
        />
      );
    }

    if (allRows.length === 0) {
      return (
        <CenteredState
          title="No routes available"
          description={`No ${direction.toLowerCase()} routes were returned for this selection`}
        />
      );
    }

    if (filteredRows.length === 0) {
      return (
        <CenteredState
          title="No matching routes"
          description="No routes match the active filters — try clearing some filters"
        />
      );
    }

    return <BgpRouteTable rows={filteredRows} direction={direction} showReflectorColumn={isCluster} />;
  };

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden p-4">
      {/* Suppressed while loading (the set is not settled yet) and when every member is down,
          since the terminal state below already says so. */}
      {!loading && !allMembersDown && downLabels.length > 0 && <DownMemberNotice labels={downLabels} />}
      {body()}
    </div>
  );
};

export default BgpTableView;
